import { INode } from "@multiweb/clients/exportImport";
import {
    IOWAttributeDesciption,
    IOWClassAttributeDescription,
    IOWEnumAttributeDescription,
    OWDataType,
} from "@multiweb/types/contracts";
import { IDisposable } from "Global/Base/IDisposable";
import { Key } from "Global/Base/Utils/EventUtils";
import { EventsController } from "../../Global/Dom/EventsController";
import { IOWClassDescription } from "../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { OWClassDescription } from "../../OW.Remoting/Remoting/OWDescriptions";
import { GenericControl } from "../UI.BaseControls/GenericControl";
import { createElement, registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { TreeClassViewRow } from "./DataGridSubcontrols/Components/TreeClassViewRow";
import { CollapseState } from "./DataGridSubcontrols/Components/TreeViewDataRow";
import { EmptyTreeDataGridMessage } from "./DataGridSubcontrols/DataGridMessages/EmptyTreeDataGridMessage";
import {
    EmptyDatagridMessageType,
    IEmptyDataGridMessage,
} from "./DataGridSubcontrols/DataGridMessages/IEmptyDataGridMessage";
import { ClassDescriptionLightDataSource } from "./DataSource/ClassDescriptionLightDataSource";

/**
 * Třída pro stromové zobrazení ClassDescription jako DataSource je použit ModelDescription
 */
export class TreeClassGrid extends HTMLElement implements IDisposable {
    public emptyMessage: IEmptyDataGridMessage;
    public dataSource: ClassDescriptionLightDataSource = new ClassDescriptionLightDataSource();
    private _dataTable: HTMLUListElement;
    private _dataLoadCounter: number = 0;
    protected _selectedDataRows: Array<TreeClassViewRow> = [];
    private _eventController: EventsController = new EventsController();
    private _isDisposed = false;

    public get dataRows(): TreeClassViewRow[] {
        return Array.from(this._dataTable.children) as TreeClassViewRow[];
    }

    public get selectedRows(): readonly TreeClassViewRow[] {
        return [...this._selectedDataRows];
    }

    public get firstSelectedRow(): TreeClassViewRow | null {
        if (this._selectedDataRows.length > 0) return this._selectedDataRows[0];
        return null;
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        GenericControl._constructor(this as HTMLElement);

        this.classList.add("tree-view-grid", "dataGrid");
        const listDiv = createElement({ classes: "p-list-div" });

        this._dataTable = createElement({ type: "ul", classes: "list-div" }) as HTMLUListElement;

        listDiv.appendChild(this._dataTable);
        this.appendChild(listDiv);

        this.emptyMessage = new EmptyTreeDataGridMessage();
        this.appendChild(this.emptyMessage);

        this._registerEventListeners();
    }

    protected connectedCallback(): void {
        this._eventController.addListener(this, "childDataRequest", (e: CustomEvent) => {
            if (e.target instanceof TreeClassViewRow) {
                this._loadClassData(e.target).then(() => e.detail.promiseResolvedFnc());
            }
        });
    }

    protected disconnectedCallback(): void {
        this._eventController.removeAllListeners();
    }

    public clearList(exclude: string[]): string[] {
        const found: string[] = [];
        for (const row of this.dataRows) {
            if (!exclude.includes(row.dataKey)) row.remove();
            else found.push(row.dataKey);
        }

        return found;
    }

    public async loadClass(classNames: string[]): Promise<void> {
        const excludes = this.clearList(classNames);
        const data = await this.dataSource.loadClassData(classNames.filter((item) => !excludes.includes(item)));

        for (const [index, classInfo] of data.entries()) {
            const row = new TreeClassViewRow();
            row.dataKey = classInfo.className;
            row.values.push(classInfo.className);

            if (this.dataRows?.length === 0) {
                if (index === 0) {
                    row.tabIndex = index;
                }
            } else {
                row.tabIndex = -1;
            }

            this._dataTable.appendChild(row.render());

            this._afterCreateRow(row, classInfo);
        }

        if (this.dataRows[0]?.tabIndex === -1) {
            this.dataRows[0].tabIndex = 0;
        }
    }

    private async _loadClassData(parentRow: TreeClassViewRow): Promise<void> {
        const [classInfo] = await this.dataSource.loadClassData(parentRow.dataKey);
        if (!classInfo) throw Error(`Class '${parentRow.dataKey}' not found!`);

        const sortedAttrs = [...classInfo.attributes].sort((a, b) => a.attributeName.localeCompare(b.attributeName));

        for (const attr of sortedAttrs) {
            const row = new TreeClassViewRow();
            row.values.push(attr.attributeName);
            row.values.push(attr.type);
            row.isLastNode = attr.type !== OWDataType.OWObject;
            row.isRequired = attr?.required ?? false;
            row.tabIndex = -1;

            if (attr.type === OWDataType.OWObject) {
                const attrClassInfo = attr as IOWClassAttributeDescription;
                row.dataKey = attrClassInfo.className;
                row.values.push(attrClassInfo.className);
            } else if (attr.type === OWDataType.enum) {
                const attrEnumnfo = attr as IOWEnumAttributeDescription;
                row.values.push(attrEnumnfo.enumName);
            }

            this._afterCreateRow(row, attr);

            parentRow.appendChild(row.render());
        }
    }

    private _registerEventListeners(): void {
        this._eventController.addListener(this, "keydown", (event) => {
            const siblingType = event.key === Key.ArrowDown ? "nextElementSibling" : "previousElementSibling";

            const focusedElement = document.activeElement;
            if (this.contains(focusedElement) && focusedElement.classList.contains("tree-class-view-row")) {
                const row = focusedElement as TreeClassViewRow;

                const rowSibling = row[siblingType];

                const toggleExpandByUser = () => {
                    row.toggleExpand();
                    row.stateChangeByUser = true;
                };

                switch (event.key) {
                    case Key.Enter:
                        event.stopImmediatePropagation();
                        toggleExpandByUser();
                        break;
                    case Key.Space:
                        event.stopImmediatePropagation();
                        event.preventDefault();

                        row.isSelected = true;
                        row.toggleChecked();
                        row.stateChangeByUser = true;

                        row.callEvent("changeByUser", {}, true);
                        break;
                    case Key.ArrowDown:
                    case Key.ArrowUp:
                        event.stopImmediatePropagation();
                        event.preventDefault();

                        if (row.state === CollapseState.expanded) {
                            if (event.key === Key.ArrowDown && row.childRows.length > 0) {
                                row.childRows[0]?.focus();
                                break;
                            }
                        }

                        if (event.key === Key.ArrowUp && !rowSibling) {
                            row.parentRow?.focus();
                            break;
                        }

                        if (event.key === Key.ArrowDown && !rowSibling) {
                            const parentSibling = row.parentRow[siblingType];

                            if (parentSibling) {
                                (parentSibling as HTMLElement)?.focus();
                                break;
                            }
                        }

                        (rowSibling as HTMLElement)?.focus();
                        break;
                    case Key.ArrowLeft:
                    case Key.ArrowRight:
                        event.stopImmediatePropagation();

                        if (event.key === Key.ArrowRight && row.state === CollapseState.collapsed) {
                            toggleExpandByUser();
                        } else if (event.key === Key.ArrowLeft && row.state === CollapseState.expanded) {
                            toggleExpandByUser();
                        }

                        break;
                    default:
                        break;
                }
            }
        });
    }

    private _afterCreateRow(row: TreeClassViewRow, data?: IOWAttributeDesciption | IOWClassDescription): void {
        this._eventController.addListener(row, "selectedRecordChanged", () => {
            while (this._selectedDataRows.length > 0) {
                this._selectedDataRows.pop().isSelected = false;
            }

            row.isSelected = true;

            this._selectedDataRows.push(row);
            this._triggerSelectionChanged(row, data);
        });
    }

    /**
     * Vyvolává událost určující, že došlo k výběru záznamu
     * @param row Řádek záznamu
     * @param data Data záznamu
     */
    protected _triggerSelectionChanged(row: TreeClassViewRow, data?: IOWAttributeDesciption | IOWClassDescription): void {
        GenericControl.callEvent(this, "SelectionChanged", { dataGrid: this, rowID: row.dataKey, data: data, row: row }, true, true);
    }

    /**
     * Stará se o zobrazení zprávy s informací, že nejsou dostupné žádné záznamy ke zobrazení
     */
    protected _updateEmptyMessageVisibility(): void {
        if (this.dataRows.length === 0) {
            this.classList.add("is-empty");
            this.emptyMessage.visible = true;

            if (this._dataLoadCounter > 0) {
                this.emptyMessage.messageType = EmptyDatagridMessageType.dataLoading;
            } else if (this.emptyMessage.messageType !== EmptyDatagridMessageType.queryModeEmpty) {
                this.emptyMessage.messageType = EmptyDatagridMessageType.initialEmpty;
            }

            return;
        }

        this.classList.remove("is-empty");
        this.emptyMessage.visible = false;
    }

    public async getObjectData(): Promise<readonly INode[]> {
        const preloadClassNames = new Set<string>();
        const selectedData = this.dataRows.filter(item => item.isChecked);

        // Zkontroluje validnost podmínek a nacte potomky pro tridy, je to delano bokem, aby se nemusel vracet kazdy clen vnitrniho stromu jako Promise
        const conditionCheckData = async (row: TreeClassViewRow, level: number = 0): Promise<void> => {
            if (row.dataKey) {
                if (!(await row.isConditionValid)) throw Error("Condition is not valid");
                else {
                    if (!row.isLastNode && row.childRows) {
                        for (const innerRow of row.childRows) {
                            if (innerRow.isChecked) await conditionCheckData(innerRow, ++level);
                        }
                    }
                }

                if (!row.openStateChangeByUser && row.childRows.length === 0) {
                    preloadClassNames.add(row.dataKey);
                }
            }
        };

        await Promise.all(selectedData.map((item) => conditionCheckData(item)));

        // Prednacteni neotevrenych trid
        const preloadedClassData = await this.dataSource.loadClassData(Array.from(preloadClassNames));
        const classMap = new Map<string, OWClassDescription>();
        let i = 0;
        for (const className of preloadClassNames) {
            classMap.set(className, preloadedClassData[i]);
            i++;
        }

        const getData = (row: TreeClassViewRow, level: number = 0): INode => {
            const node: INode = {
                name: row.values?.[0],
                isObject: !!row.dataKey,
            };
            if (node.isObject) {
                if (row.condition.length > 0) {
                    node.condition = row.condition;
                }
                node.className = row.dataKey;
                node.children = [];

                if (row.childRows?.length > 0) {
                    for (const innerRow of row.childRows) {
                        if (innerRow.isChecked) node.children.push(getData(innerRow, ++level));
                    }
                } else {
                    if (!row.openStateChangeByUser && level === 0) {
                        node.children = classMap.get(row.dataKey).attributes.map(item => {
                            const innerNode: INode = {
                                name: item.attributeName,
                                isObject: item.type === OWDataType.OWObject,
                            };
                            if (innerNode.isObject) {
                                innerNode.className = (item as IOWClassAttributeDescription).className;
                                innerNode.children = [];
                            }

                            return innerNode;
                        });
                    }
                }
            }

            return node;
        };

        return selectedData.map(getData);
    }

    public dispose(): void {
        if (this._isDisposed) {
            return;
        }

        this._eventController.removeAllListeners();
        for (const row of this.dataRows) {
            row.dispose();
        }

        this._isDisposed = true;
    }
}

registerCustomElement("tsc-TreeClassGrid", TreeClassGrid);
